import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import ReactSignatureCanvas from 'react-signature-canvas';
import {
    getCarePlanSignatureStatus,
    saveCarePlanSignatures,
    setShowSignatureModal,
    setSignatureType,
    setVerbalConsentName,
} from '../../../store/carePlanUpdate.slice';

import { CarePlanSignatureTypeEnum } from '../../../Enum/CarePlanSignatureTypeEnum';
import { RootState } from '../../../reducers';

const SignatureModal = () => {
    const dispatch = useAppDispatch();
    const providerCanvasRef = useRef<any>(null);
    const nonProviderCanvasRef = useRef<any>(null);
    const { reviewSignChecked, showSignatureModal, signatureType, verbalConsentName, integratedCarePlan } = useSelector(
        (state: RootState) => state.carePlanUpdate
    );
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const [providerHasSignature, setProviderHasSignature] = useState(false);
    const [nonProviderHasSignature, setNonProviderHasSignature] = useState(false);

    const checkedGoalIds = Object.keys(reviewSignChecked).filter((key) => reviewSignChecked[key]);

    const handleWindowResize = () => {
        if (dialogContentRef.current) {
            setCanvasSize({
                width: dialogContentRef.current.clientWidth,
                height: dialogContentRef.current.clientHeight,
            });
        }
    };

    useEffect(() => {
        handleWindowResize();
        window.addEventListener('resize', handleWindowResize, false);
        return () => {
            window.removeEventListener('resize', handleWindowResize, false);
        };
    }, []);

    const dialogContent = document.getElementById('dialog-content');

    const [canvasSize, setCanvasSize] = useState({ width: dialogContent?.offsetWidth || 800, height: 180 });

    const nonProviderType = signatureType === CarePlanSignatureTypeEnum.Provider ? CarePlanSignatureTypeEnum.Member : signatureType;

    const handleClose = () => {
        dispatch(setShowSignatureModal(false));
        providerCanvasRef.current?.clear();
        nonProviderCanvasRef.current?.clear();
        dispatch(setVerbalConsentName(''));
        setProviderHasSignature(false);
        setNonProviderHasSignature(false);
    };

    const handleAccept = () => {
        const providerSignature =
            providerCanvasRef.current && !providerCanvasRef.current.isEmpty() ? providerCanvasRef.current.getTrimmedCanvas().toDataURL('image/png') : null;
        const nonProviderSignature =
            nonProviderType === CarePlanSignatureTypeEnum.VerbalConsent
                ? verbalConsentName.trim()
                : nonProviderCanvasRef.current && !nonProviderCanvasRef.current.isEmpty()
                ? nonProviderCanvasRef.current.getTrimmedCanvas().toDataURL('image/png')
                : '';
        const hasProvider = Boolean(providerSignature);
        const hasNonProvider = Boolean(nonProviderSignature && (nonProviderType !== CarePlanSignatureTypeEnum.VerbalConsent || nonProviderSignature.trim()));

        if (!hasProvider && !hasNonProvider) {
            return;
        }

        const provider = hasProvider ? providerSignature : null;
        const nonProvider =
            hasNonProvider && nonProviderType !== CarePlanSignatureTypeEnum.VerbalConsent
                ? {
                      signature: nonProviderSignature,
                      signatureType: nonProviderType,
                  }
                : null;
        dispatch(
            saveCarePlanSignatures(provider, nonProvider, checkedGoalIds, () => {
                dispatch(getCarePlanSignatureStatus(integratedCarePlan?.MemberId || ''));
            })
        );
        handleClose();
    };

    const handleNonProviderChange = (event: SelectChangeEvent<number>) => {
        const nextType = Number(event.target.value) as CarePlanSignatureTypeEnum;
        dispatch(setSignatureType(nextType));
        nonProviderCanvasRef.current?.clear();
        dispatch(setVerbalConsentName(''));
        setNonProviderHasSignature(false);
    };

    const providerReady = providerHasSignature;
    const nonProviderReady = nonProviderType === CarePlanSignatureTypeEnum.VerbalConsent ? verbalConsentName.trim().length > 0 : nonProviderHasSignature;

    return (
        <Dialog
            open={showSignatureModal}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Signature</DialogTitle>
            <DialogContent id="dialog-content">
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Provider Signature
                    </Typography>
                    <Box ref={dialogContentRef} border={1}>
                        <ReactSignatureCanvas
                            ref={providerCanvasRef}
                            penColor="blue"
                            onEnd={() => setProviderHasSignature(true)}
                            canvasProps={{
                                width: canvasSize.width,
                                height: canvasSize.height,
                                className: 'sigCanvas',
                            }}
                        />
                    </Box>
                </Box>
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Member Signature
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="member-type-label">Signature Type</InputLabel>
                        <Select<number> labelId="member-type-label" value={nonProviderType} label="Signature Type" onChange={handleNonProviderChange}>
                            <MenuItem value={CarePlanSignatureTypeEnum.Member}>Member</MenuItem>
                            <MenuItem value={CarePlanSignatureTypeEnum.GuardianOrCareGiver}>Guardian/Caregiver</MenuItem>
                            <MenuItem value={CarePlanSignatureTypeEnum.HealthCareDecisionMaker}>Decision Maker</MenuItem>
                            <MenuItem value={CarePlanSignatureTypeEnum.VerbalConsent}>Verbal Consent</MenuItem>
                        </Select>
                    </FormControl>
                    {nonProviderType !== CarePlanSignatureTypeEnum.VerbalConsent ? (
                        <Box border={1}>
                            <ReactSignatureCanvas
                                ref={nonProviderCanvasRef}
                                penColor="blue"
                                onEnd={() => setNonProviderHasSignature(true)}
                                canvasProps={{
                                    width: canvasSize.width,
                                    height: canvasSize.height,
                                    className: 'sigCanvas',
                                }}
                            />
                        </Box>
                    ) : (
                        <TextField fullWidth label="Name" value={verbalConsentName} onChange={(e) => dispatch(setVerbalConsentName(e.target.value))} />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleClose}>
                    Cancel
                </Button>
                <Button disabled={!providerReady && !nonProviderReady} variant="contained" onClick={handleAccept}>
                    Accept
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SignatureModal;
